import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import DoneIcon from "@material-ui/icons/Done";
import ErrorIcon from "@material-ui/icons/Error";
import BlockIcon from "@material-ui/icons/Block";
import { makeStyles } from "@material-ui/core/styles";
import URLSearchParams from "@ungap/url-search-params";

import { FormatDate, FormatAmount } from "../../../utils/Formatter";
import { Status, DropReason } from "../../../model/Charge";
import TestIndicator from "../../common/TestIndicator";

const useStyles = makeStyles(({ palette }) => {
  return {
    root: {
      color: palette.primary.contrastText,
      background: `linear-gradient(36deg, ${palette.primary.dark} 0%, ${palette.primary.main} 54%, ${palette.primary.light} 100%)`,
      padding: "25px",
      alignItems: "center",
      display: "flex",
      position: "relative",
      "& svg": {
        fontSize: "2rem",
        padding: "10px"
      }
    }
  };
});

const PaymentResult = ({ charge }) => {
  const formRef = useRef(null);
  const classes = useStyles();

  const getIcon = status => {
    switch (status) {
      case Status.PAID:
      case Status.LATE_SPEND:
      case Status.DOUBLE_SPEND:
      case Status.REFUNDED:
      case Status.REFUNDED_PARTIALLY:
        return <DoneIcon />;
      case Status.DROPPED:
        return <BlockIcon />;
      default:
        return <ErrorIcon />;
    }
  };

  const getMessage = status => {
    switch (status) {
      case Status.PAID:
      case Status.LATE_SPEND:
      case Status.DOUBLE_SPEND:
      case Status.REFUNDED:
      case Status.REFUNDED_PARTIALLY:
        return "Successful payment";
      case Status.DROPPED:
        return "The charge has been cancelled";
      default:
        return "An unknown error occured. Payment state is inconsistent.";
    }
  };

  const getDroppedChargeMessage = dropReason => {
    switch (dropReason) {
      case DropReason.TOO_MANY_ATTEMPTS:
        return "Too many failed attempts";
      case DropReason.EXPIRED:
        return "Your payment has expired";
      case DropReason.DROPPED_OUT:
        return "Charge cancelled by the merchant";
      default:
        return "";
    }
  };

  const getReturnFields = () => {
    const returnFields = [];
    const returnData = charge.formDataReturnData;
    if (charge.returnUrl) {
      const urlArray = charge.returnUrl.split("?");
      const [, queryParams] = urlArray;
      if (queryParams) {
        const searchParams = new URLSearchParams(queryParams);

        searchParams.forEach((value, key) =>
          returnFields.push(
            <input type="hidden" name={key} value={value} key={value} />
          )
        );
      }
    }

    if (!returnData) return returnFields;
    Object.entries(returnData).forEach(field => {
      if (field[1]) {
        returnFields.push(
          <input
            type="hidden"
            name={field[0]}
            value={field[1]}
            key={field[0]}
          />
        );
      }
    });
    return returnFields;
  };

  const startFormAutoSubmitionTimeout = delay => {
    setTimeout(() => {
      formRef.current.submit();
    }, delay * 1000);
  };

  useEffect(() => {
    switch (charge.status) {
      case Status.PAID:
        if (charge.redirectSuccessTimeout)
          startFormAutoSubmitionTimeout(charge.redirectSuccessTimeout);
        break;
      case Status.DROPPED:
        if (charge.redirectErrorTimeout)
          startFormAutoSubmitionTimeout(charge.redirectErrorTimeout);
        break;
      default:
        break;
    }
  }, []);

  return (
    <Grid container direction="column">
      <div className={classes.root}>
        {getIcon(charge.status)}
        <div>
          <Typography variant="h6" align="center">
            {getMessage(charge.status)}
          </Typography>
          {charge.status === Status.DROPPED && (
            <Typography variant="caption" align="center">
              {getDroppedChargeMessage(charge.dropReason)}
            </Typography>
          )}
        </div>
        {charge.testMode && <TestIndicator />}
      </div>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>Shop Name:</TableCell>
            <TableCell>{charge.shopName}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Shop ID:</TableCell>
            <TableCell>{charge.shopId}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Order ID:</TableCell>
            <TableCell>{charge.orderId}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Amount:</TableCell>
            <TableCell>
              {FormatAmount(charge.amount, charge.currency)}
            </TableCell>
          </TableRow>
          {charge.refunded ? (
            <TableRow>
              <TableCell>Refunded:</TableCell>
              <TableCell>
                {FormatAmount(charge.refunded, charge.currency)}
              </TableCell>
            </TableRow>
          ) : (
            ""
          )}
          {charge.paid && (
            <TableRow>
              <TableCell>Payment Mode:</TableCell>
              <TableCell>{charge.paymentFamily}</TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>Date:</TableCell>
            <TableCell>{FormatDate(charge.date)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Grid container justify="center">
        <form
          action={charge.returnUrl}
          method={charge.returnMethod}
          ref={formRef}
        >
          {getReturnFields()}
          <Button
            color="primary"
            type="submit"
            style={{ marginTop: "15px", marginBottom: "15px" }}
          >
            Return to shop
          </Button>
        </form>
      </Grid>
    </Grid>
  );
};

PaymentResult.propTypes = {
  charge: PropTypes.shape({
    status: PropTypes.string.isRequired,
    shopName: PropTypes.string.isRequired,
    shopId: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    paid: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    date: PropTypes.instanceOf(Date).isRequired,
    refunded: PropTypes.number,
    returnUrl: PropTypes.string.isRequired,
    returnMethod: PropTypes.string.isRequired,
    formDataReturnData: PropTypes.shape().isRequired,
    redirectSuccessTimeout: PropTypes.string,
    redirectErrorTimeout: PropTypes.string,
    dropReason: PropTypes.string.isRequired,
    testMode: PropTypes.bool.isRequired,
    paymentFamily: PropTypes.string
  }).isRequired
};

export default PaymentResult;
